import { normalizeKnowledgeFacts } from '../knowledgeItems/knowledgeFactNormalizer';
import { resolveSupportProfileFacts } from '../knowledgeItems/supportProfileResolver';
import { getCareLensKey } from '../../utils/careLens';
import { normalizeChapterProvenance } from './chapterProvenance';
import { resolveKnowledgeSourceAction } from './knowledgeSourceAction';
import { buildProfileSourcePresentation } from './profileSourcePresentation';
import {
  PROFILE_PLACEMENT_CHAPTERS,
  isExplicitProfilePlacement,
  isExplicitlyPlacedInAnotherChapter,
} from './profilePlacementMetadata';
import { resolveKnowledgeFactsForProfileChapter } from './profileKnowledgeProjection';
import { PROFILE_CHAPTER_TITLES } from '../../constants/profileChapterTitles';

export const COMMUNICATION_DOMAIN_ID = 'DM_02';

const GROUP_DEFINITIONS = Object.freeze([
  { id: 'communication_methods', title: 'How they communicate' },
  { id: 'understanding_processing', title: 'Understanding and processing' },
  { id: 'expressing_choices_needs', title: 'Choices and needs' },
  { id: 'helpful_approaches', title: 'What helps' },
  { id: 'avoid_assumptions', title: 'Avoid or do not assume' },
]);

const cleanText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();
const valueKey = (value = '') => cleanText(value).toLowerCase();

const toCaregiverAction = (value = '') => {
  const text = cleanText(value).replace(/[.]+$/, '');
  if (/^uses\b/i.test(text)) return text.replace(/^uses\b/i, 'Use');
  if (/^needs?\b.*\bprocess/i.test(text)) return text.replace(/^needs?\b/i, 'Allow');
  return text;
};

const getHandbookCopy = (value = '') => {
  const text = cleanText(value).replace(/[.]+$/, '');
  const firstClause = text.split(/\s+(?:—|–)\s+|[.;]\s+/)[0];

  if (/\baac\b/i.test(text)) {
    return {
      headline: 'Uses AAC',
      explanation: 'This is an important way they communicate choices, needs, or requests.',
    };
  }
  if (/\bprocess|\bwait\b.*\bseconds?\b/i.test(text)) {
    return {
      headline: 'Allow processing time',
      explanation: `${text}. Give them time to respond before repeating or rephrasing.`,
    };
  }
  if (/one[- ]step/i.test(text)) {
    return {
      headline: 'One-step instructions',
      explanation: 'Keep each instruction short, clear, and focused on one thing at a time.',
    };
  }

  const headline = firstClause.length <= 62 ? firstClause : `${firstClause.slice(0, 59).trim()}…`;
  return {
    headline,
    explanation: headline.toLowerCase() === text.toLowerCase()
      ? 'Keep this guidance in mind during conversations and care routines.'
      : `${text}.`,
  };
};

const classifyCommunicationValue = (value = '') => {
  const text = cleanText(value).toLowerCase();
  const groupIds = [];

  if (/\b(?:aac|pecs|speech|speaks?|sign language|signs?|gestures?|eye gaze|picture cards?|communication (?:board|device)|facial expression|body movement|nonverbal|verbal)\b/.test(text)) {
    groupIds.push('communication_methods');
  }
  if (/\b(?:process(?:ing)? time|wait\b.*\bseconds?|understands?|one[- ]step|simple instructions?|written (?:information|reminders?)|preferred language|one question at a time|literal language|time to respond)\b/.test(text)) {
    groupIds.push('understanding_processing');
  }
  if (/\b(?:yes|no|choice|request|ask for help|pain|discomfort|refus|stop|desired object|needs?)\b/.test(text)) {
    groupIds.push('expressing_choices_needs');
  }
  if (/^(?:use|offer|give|allow|wait|explain|ask|speak|show|keep)\b/.test(text)
    || /\b(?:helps?|works well|responds well)\b/.test(text)) {
    groupIds.push('helpful_approaches');
  }
  if (/^(?:avoid|do not|don't|never)\b/.test(text)
    || /\bdo not assume\b/.test(text)) {
    groupIds.push('avoid_assumptions');
  }

  return groupIds.length ? [...new Set(groupIds)] : ['communication_methods'];
};

const buildLegacyItem = ({ id, value, sourcePath }) => ({
  id,
  value: cleanText(value),
  groupIds: classifyCommunicationValue(value),
  sourceKind: 'legacy_profile',
  confidence: 'high',
  needsQuickCheck: false,
  pinned: false,
  synthetic: true,
  provenance: [{ sourceCollection: 'children', sourcePath }],
});

const buildKnowledgeItem = (fact = {}) => ({
  id: fact.id,
  value: cleanText(fact.summary || fact.title),
  groupIds: classifyCommunicationValue(fact.summary || fact.title),
  sourceKind: 'knowledge',
  confidence: fact.confidence,
  needsQuickCheck: Boolean(fact.needsQuickCheck),
  pinned: fact.profileVisibilityOverride === 'pin',
  synthetic: false,
  provenance: fact.sourceRefs || [],
  profilePlacementMode: fact.profilePlacementMode,
  profileChapterId: fact.profileChapterId,
  capturedBy: fact.capturedBy,
  extractionVersion: fact.extractionVersion,
  sourcePresentation: buildProfileSourcePresentation(fact),
});

const deduplicateItems = (items = []) => {
  const output = [];
  const indexByValue = new Map();

  items.forEach((item) => {
    const key = valueKey(item.value);
    if (!key) return;
    const existingIndex = indexByValue.get(key);
    if (existingIndex === undefined) {
      indexByValue.set(key, output.length);
      output.push(item);
      return;
    }
    if (output[existingIndex].synthetic && !item.synthetic) {
      output[existingIndex] = item;
    }
  });

  return output;
};

const buildProvenance = (items = []) => {
  const sourceRefs = items.flatMap((item) => item.provenance || []);
  return normalizeChapterProvenance(sourceRefs, 'communication-source');
};

// DM_02 pilot only: attach sourceAction only when ownership is unambiguous.
// Legacy/synthetic items (no real Knowledge doc behind them) never get one -
// there is nothing a Knowledge write path could update. The actual
// classification-by-sourceType logic is shared across every domain that has
// adopted source routing; see knowledgeSourceAction.js.
const resolveEntrySourceAction = (item) => {
  if (item.synthetic || item.sourceKind !== 'knowledge') return undefined;
  return resolveKnowledgeSourceAction({ ...item, sourceRefs: item.provenance });
};

const getPresentation = ({ child = {}, careContext = {}, ageGroup = '' } = {}) => {
  const resolvedCareContext = careContext || child.careContext || {};
  const lens = getCareLensKey({
    selectedConditions: child.conditions || [],
    careContext: resolvedCareContext,
  });
  return {
    lens,
    ageGroup: ageGroup || resolvedCareContext.ageGroup || 'unknown',
    displayTitle: PROFILE_CHAPTER_TITLES.COMMUNICATION,
    groupOrder: GROUP_DEFINITIONS.map((group) => group.id),
    emphasis: [],
    tone: 'communication',
  };
};

export const buildCommunicationDomain = ({
  child = {},
  knowledgeFacts = [],
  careContext = child.careContext || {},
  ageGroup = careContext?.ageGroup || '',
} = {}) => {
  const normalizedFacts = normalizeKnowledgeFacts(knowledgeFacts);
  const resolvedSupportProfile = resolveSupportProfileFacts({
    knowledgeFacts: normalizedFacts,
    legacySupportProfile: child.supportProfile || child.supportGoals || {},
  });
  const fieldResolvedFacts = [
    ...(resolvedSupportProfile.factsByField.communicationStyle || []),
    ...(resolvedSupportProfile.factsByField.communicationNotes || []),
  ].filter((fact) => !isExplicitlyPlacedInAnotherChapter(fact, PROFILE_PLACEMENT_CHAPTERS.COMMUNICATION));
  const resolvedIds = new Set(fieldResolvedFacts.map((fact) => fact.id));
  const projectedFacts = resolveKnowledgeFactsForProfileChapter(
    normalizedFacts,
    PROFILE_PLACEMENT_CHAPTERS.COMMUNICATION
  ).filter((fact) => !resolvedIds.has(fact.id));
  const resolvedFacts = [...fieldResolvedFacts, ...projectedFacts];
  const supportItems = resolvedFacts.map((fact) => (
    fact.synthetic
      ? buildLegacyItem({
        id: fact.id,
        value: fact.summary,
        sourcePath: `supportProfile.${fact.profileField}`,
      })
      : buildKnowledgeItem(fact)
  ));
  const communicationNeeds = Array.isArray(child.medicalProfile?.communicationNeeds)
    ? child.medicalProfile.communicationNeeds
    : [];
  const medicalItems = communicationNeeds.map((value, index) => buildLegacyItem({
    id: `legacy-medicalProfile:communicationNeeds:${index}`,
    value,
    sourcePath: 'medicalProfile.communicationNeeds',
  }));
  const items = deduplicateItems([...medicalItems, ...supportItems]);
  const presentation = getPresentation({ child, careContext, ageGroup });
  const prioritizedItems = [...items].sort(
    (left, right) => Number(Boolean(right.pinned)) - Number(Boolean(left.pinned))
  );
  const briefingItems = prioritizedItems.slice(0, 3).map((item) => ({
    id: `briefing-${item.id}`,
    text: toCaregiverAction(item.value),
    explainedByEntryIds: [`entry-${item.id}`],
  }));
  const assignedItemIds = new Set();
  const handbookGroups = GROUP_DEFINITIONS.map((group) => {
    const groupItems = items.filter((item) => (
      !assignedItemIds.has(item.id) && item.groupIds.includes(group.id)
    ));
    groupItems.forEach((item) => assignedItemIds.add(item.id));
    return {
      id: group.id,
      title: group.title,
      entries: groupItems.map((item) => ({
        id: `entry-${item.id}`,
        knowledgeFactId: item.synthetic ? undefined : item.id,
        ...getHandbookCopy(item.value),
        fullText: item.value,
        provenance: item.synthetic ? undefined : buildProvenance([item]),
        sourcePresentation: item.synthetic ? undefined : item.sourcePresentation,
        sourceAction: resolveEntrySourceAction(item),
      })),
    };
  }).filter((group) => group.entries.length);

  return {
    id: 'communication',
    title: presentation.displayTitle,
    caregiverQuestion: 'How should I communicate with this person?',
    briefing: {
      items: briefingItems,
      provenance: buildProvenance(items.filter((item) => !item.synthetic)),
    },
    handbook: {
      groups: handbookGroups,
    },
    emptyState: {
      title: 'No communication guidance yet',
      description: 'Add how they express needs, understand language, and communicate choices.',
    },
    metadata: {
      domainId: COMMUNICATION_DOMAIN_ID,
      careFocus: presentation.lens,
      ageGroup: presentation.ageGroup,
      contractVersion: '1',
    },
  };
};
